import { KSUID } from '@owpz/ksuid';
import { Edge } from '@xyflow/react';
import { atom } from 'jotai';

import type { AutomationNode } from '../_components/editor/nodes/types';
import { isTriggerNodeType } from '../_components/editor/nodes/types';

export type ClipboardContent = {
  nodes: AutomationNode[];
  edges: Edge[];
  copiedAt: number;
};

const CLIPBOARD_STORAGE_KEY = 'automation-flow-clipboard';

/**
 * Helper function to retrieve clipboard content from sessionStorage
 */
const getStoredClipboard = (): ClipboardContent | null => {
  try {
    const stored = sessionStorage.getItem(CLIPBOARD_STORAGE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored) as ClipboardContent;

    // Validate the structure
    if (
      !parsed.nodes ||
      !Array.isArray(parsed.nodes) ||
      !parsed.edges ||
      !Array.isArray(parsed.edges) ||
      typeof parsed.copiedAt !== 'number'
    ) {
      return null;
    }

    return parsed;
  } catch (error) {
    console.warn('Failed to parse clipboard from sessionStorage:', error);
    return null;
  }
};

/**
 * Helper function to store clipboard content to sessionStorage
 */
const storeClipboard = (content: ClipboardContent): void => {
  try {
    sessionStorage.setItem(CLIPBOARD_STORAGE_KEY, JSON.stringify(content));
  } catch (error) {
    console.warn('Failed to store clipboard to sessionStorage:', error);
  }
};

/**
 * Clipboard atom - stores the current clipboard content
 * Initialized from sessionStorage to persist across page refreshes within the same session
 */
export const $clipboard = atom<ClipboardContent | null>(getStoredClipboard());

/**
 * Computed atom - returns true if there is content in the clipboard
 */
export const $hasClipboardContent = atom((get) => {
  const clipboard = get($clipboard);
  return clipboard !== null && clipboard.nodes.length > 0;
});

/**
 * Action atom to copy selected nodes and their edges
 * Filters out trigger nodes (which cannot be copied)
 * Only includes edges where BOTH source and target nodes are in the selection
 */
export const $copyNodes = atom(
  null,
  (get, set, params: { selectedNodes: AutomationNode[]; allEdges: Edge[] }) => {
    const { selectedNodes, allEdges } = params;

    // Filter out trigger nodes - they cannot be copied
    const copyableNodes = selectedNodes.filter(
      (node) => !isTriggerNodeType(node.type || ''),
    );

    if (copyableNodes.length === 0) {
      // Nothing to copy
      return;
    }

    // Create a set of copyable node IDs for fast lookup
    const copyableNodeIds = new Set(copyableNodes.map((node) => node.id));

    // Only include edges where BOTH source and target are in the copyable selection
    const copyableEdges = allEdges.filter(
      (edge) =>
        copyableNodeIds.has(edge.source) && copyableNodeIds.has(edge.target),
    );

    const clipboardContent: ClipboardContent = {
      nodes: copyableNodes,
      edges: copyableEdges,
      copiedAt: Date.now(),
    };

    // Store to atom and sessionStorage
    set($clipboard, clipboardContent);
    storeClipboard(clipboardContent);
  },
);

/**
 * Action atom to paste nodes from clipboard
 * Generates new unique IDs for all nodes and edges
 * Updates edge references to point to new node IDs
 * Calculates position offset for pasted nodes
 */
export const $pasteNodes = atom(
  null,
  (
    get,
    _set,
    params?: {
      position?: { x: number; y: number };
      offset?: { x: number; y: number };
    },
  ): { nodes: AutomationNode[]; edges: Edge[] } | null => {
    const clipboard = get($clipboard);

    if (!clipboard || clipboard.nodes.length === 0) {
      return null;
    }

    const { position, offset = { x: 50, y: 50 } } = params || {};

    // Create a mapping from old node IDs to new node IDs
    const idMap = new Map<string, string>();

    // Generate new IDs for all nodes
    clipboard.nodes.forEach((node) => {
      idMap.set(node.id, `node_${KSUID.random()}`);
    });

    // Calculate position offset
    let positionOffset = offset;

    if (position && clipboard.nodes.length > 0) {
      // If a specific position is provided, calculate offset to place the first node there
      // Find the top-left position of the copied nodes
      const minX = Math.min(...clipboard.nodes.map((n) => n.position.x));
      const minY = Math.min(...clipboard.nodes.map((n) => n.position.y));

      positionOffset = {
        x: position.x - minX,
        y: position.y - minY,
      };
    }

    // Create new nodes with updated IDs and positions
    const newNodes: AutomationNode[] = clipboard.nodes.map((node) => {
      const newId = idMap.get(node.id) ?? `node_${KSUID.random()}`;

      return {
        ...node,
        id: newId,
        position: {
          x: node.position.x + positionOffset.x,
          y: node.position.y + positionOffset.y,
        },
        // Ensure the node is not selected by default
        selected: false,
      };
    });

    // Create new edges with updated IDs and source/target references
    const newEdges: Edge[] = clipboard.edges.map((edge) => {
      const newId = `edge_${KSUID.random()}`;
      const newSource = idMap.get(edge.source);
      const newTarget = idMap.get(edge.target);

      // Both source and target should exist in the idMap
      // If not, this edge won't be valid (shouldn't happen due to filtering during copy)
      if (!newSource || !newTarget) {
        console.warn(
          `Edge ${edge.id} references nodes not in clipboard:`,
          edge.source,
          edge.target,
        );
        return {
          ...edge,
          id: newId,
        };
      }

      return {
        ...edge,
        id: newId,
        source: newSource,
        target: newTarget,
        // Ensure the edge is not selected by default
        selected: false,
      };
    });

    return {
      nodes: newNodes,
      edges: newEdges,
    };
  },
);

/**
 * Action atom to clear the clipboard
 */
export const $clearClipboard = atom(null, (_get, set) => {
  set($clipboard, null);
  try {
    sessionStorage.removeItem(CLIPBOARD_STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear clipboard from sessionStorage:', error);
  }
});
